import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  Package, Clock, CheckCircle, Truck, ChefHat, 
  RefreshCw, Building2, Boxes, X, Search,
  Filter, AlertCircle, BarChart3, TrendingUp,
  ShoppingCart
} from 'lucide-react'
import { api } from '../../../lib/api'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { getCacheStale, setCache, removeCache, isCacheValid, getCacheAge } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'
import { Order, Employee, Store } from '../types'
import { OrderCard } from '../components/OrderCard'
import { EmployeeCard } from '../components/EmployeeCard'
import { useScrollHideHeader } from '../hooks/useScrollHideHeader'
import { useOrderUpdates } from '../../../hooks/useWebSocket'

const DispatcherDashboard = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [chefs, setChefs] = useState<Employee[]>([])
  const [deliveryDrivers, setDeliveryDrivers] = useState<Employee[]>([])
  const [store, setStore] = useState<Store | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [assigningOrder, setAssigningOrder] = useState<string | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignType, setAssignType] = useState<'chef' | 'delivery' | null>(null)
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [loadingFlags, setLoadingFlags] = useState({
    store: false,
    orders: false,
    employees: false
  })
  const lastRefreshRef = useRef<number>(0)
  const initialLoadRef = useRef<boolean>(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  
  const isHeaderVisible = useScrollHideHeader()

  // Get storeId from URL params or user object
  const storeId = params.storeId || user?.storeId

  // Definir funciones primero
  const loadStore = useCallback(async (silent = false) => {
    if (!storeId) return
    if (loadingFlags.store) return // Evitar múltiples llamadas simultáneas

    const cacheKey = `dispatcher_store_${storeId}`
    
    // Verificar cache primero - si es válido y reciente, NO hacer request
    const cachedStore = getCacheStale<Store>(cacheKey)
    if (cachedStore) {
      setStore(cachedStore)
      // Si el cache es válido y reciente (menos de 10 minutos), NO hacer request
      if (isCacheValid(cacheKey, 10 * 60 * 1000)) {
        return // Cache válido, no hacer request
      }
    }

    try {
      setLoadingFlags(prev => ({ ...prev, store: true }))
      const response = await api.get(`/stores/${storeId}`)
      if (response.data.success) {
        setStore(response.data.data)
        setCache(cacheKey, response.data.data, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading store:', error)
      
      // Si hay timeout o error de red y tenemos cache, usar cache sin mostrar error
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStore) {
        return
      }
      
      // Solo mostrar error si no tenemos cache y no es un refresh silencioso
      if (!silent && !cachedStore) {
        if (error.code === 'ECONNABORTED') {
          toast.error('La consulta está tardando demasiado. Usando datos en caché.')
        } else {
          toast.error('Error al cargar información del negocio')
        }
      }
    } finally {
      setLoadingFlags(prev => ({ ...prev, store: false }))
    }
  }, [storeId, loadingFlags.store])

  const loadOrders = useCallback(async (silent = false) => {
    if (!storeId) return
    if (loadingFlags.orders) return // Evitar múltiples llamadas simultáneas

    const cacheKey = `dispatcher_orders_${storeId}`
    
    // Verificar cache primero - si es válido y reciente, NO hacer request
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      // Normalizar items del cache también
      const normalizedCachedOrders = cachedOrders.map((order: Order) => ({
        ...order,
        items: (order.items || []).map((item: any) => ({
          ...item,
          unit_price: Number(item.unit_price || item.unitPrice || 0),
          quantity: Number(item.quantity || 0)
        }))
      }))
      setOrders(normalizedCachedOrders)
      
      // Si el cache es válido y reciente:
      // - En refresh silencioso: solo si tiene menos de 60 segundos
      // - En carga normal: solo si tiene menos de 30 segundos
      const maxAge = silent ? 60 * 1000 : 30 * 1000
      if (isCacheValid(cacheKey, maxAge)) {
        return // Cache válido, no hacer request
      }
    }

    try {
      setLoadingFlags(prev => ({ ...prev, orders: true }))
      const response = await api.get(`/orders/store/${storeId}?limit=100`)
      if (response.data.success) {
        const normalizedOrders = (response.data.data || []).map((order: any) => ({
          id: order.id,
          store_id: order.store_id || order.storeId,
          customer_name: order.customer_name || order.customerName,
          customer_phone: order.customer_phone || order.customerPhone,
          status: order.status,
          total_amount: Number(order.total_amount || order.totalAmount) || 0,
          delivery_fee: Number(order.delivery_fee || order.deliveryFee) || 0,
          estimated_delivery_time: Number(order.estimated_delivery_time || order.estimatedDeliveryTime) || 0,
          assigned_chef_id: order.assigned_chef_id || order.assignedChefId,
          assigned_delivery_id: order.assigned_delivery_id || order.assignedDeliveryId,
          assigned_chef: order.assigned_chef || order.assignedChef,
          assigned_delivery: order.assigned_delivery || order.assignedDelivery,
          created_at: order.created_at || order.createdAt,
          items: (order.items || []).map((item: any) => ({
            ...item,
            unit_price: Number(item.unit_price || item.unitPrice || 0),
            quantity: Number(item.quantity || 0)
          })),
          delivery_address: order.delivery_address || order.deliveryAddress,
          priority: order.priority || false
        }))
        setOrders(normalizedOrders)
        setCache(cacheKey, normalizedOrders, 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading orders:', error)
      
      // Si hay timeout o error de red y tenemos cache, usar cache
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        if (!silent) {
          showOfflineToast()
        }
        return
      }
      
      // Solo mostrar error si no tenemos cache y no es un refresh silencioso
      if (!silent && (!cachedOrders || cachedOrders.length === 0)) {
        if (error.code === 'ECONNABORTED') {
          toast.error('La consulta está tardando demasiado. Usando datos en caché.')
        } else {
          toast.error('Error al cargar pedidos')
        }
      }
    } finally {
      setLoadingFlags(prev => ({ ...prev, orders: false }))
    }
  }, [storeId, loadingFlags.orders])

  const loadEmployees = useCallback(async (silent = false) => {
    if (!storeId) return
    if (loadingFlags.employees) return // Evitar múltiples llamadas simultáneas

    const cacheKey = `dispatcher_employees_${storeId}`
    
    // Verificar cache primero - si es válido y reciente, NO hacer request
    const cachedEmployees = getCacheStale<Employee[]>(cacheKey)
    if (cachedEmployees && cachedEmployees.length > 0) {
      setChefs(cachedEmployees.filter((e: Employee) => e.type === 'chef' && e.is_active))
      setDeliveryDrivers(cachedEmployees.filter((e: Employee) => e.type === 'delivery' && e.is_active))
      
      // Si el cache es válido y reciente (menos de 10 minutos), NO hacer request
      if (isCacheValid(cacheKey, 10 * 60 * 1000)) {
        return // Cache válido, no hacer request
      }
    }

    try {
      setLoadingFlags(prev => ({ ...prev, employees: true }))
      const response = await api.get(`/auth/store/${storeId}/employees`)
      if (response.data.success) {
        const employees = response.data.data || []
        setChefs(employees.filter((e: Employee) => e.type === 'chef' && e.is_active))
        setDeliveryDrivers(employees.filter((e: Employee) => e.type === 'delivery' && e.is_active))
        setCache(cacheKey, employees, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading employees:', error)
      
      // Si hay timeout o error de red y tenemos cache, usar cache sin mostrar error
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedEmployees) {
        return
      }
      
      // Solo mostrar error si no tenemos cache y no es un refresh silencioso
      if (!silent && (!cachedEmployees || cachedEmployees.length === 0)) {
        if (error.code === 'ECONNABORTED') {
          toast.error('La consulta está tardando demasiado. Usando datos en caché.')
        } else {
          toast.error('Error al cargar empleados')
        }
      }
    } finally {
      setLoadingFlags(prev => ({ ...prev, employees: false }))
    }
  }, [storeId, loadingFlags.employees])

  const loadData = useCallback(async (silent = false) => {
    if (!storeId) return
    if (refreshing && !silent) return // Evitar múltiples llamadas simultáneas
    
    // Verificar si ya se hizo un refresh recientemente (últimos 5 segundos)
    const now = Date.now()
    if (now - lastRefreshRef.current < 5000 && silent) {
      return // Muy reciente, saltar
    }

    try {
      if (!silent) {
        setRefreshing(true)
      }
      lastRefreshRef.current = now
      
      await Promise.all([
        loadStore(silent),
        loadOrders(silent),
        loadEmployees(silent)
      ])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
      if (!silent) {
        setRefreshing(false)
      }
    }
  }, [storeId, refreshing, loadStore, loadOrders, loadEmployees])

  useEffect(() => {
    if (storeId && !initialLoadRef.current) {
      initialLoadRef.current = true
      loadData()
      
      // Auto-refresh every 60 seconds, pero solo si no hay una carga en progreso
      intervalRef.current = setInterval(() => {
        const now = Date.now()
        // Solo refrescar si han pasado al menos 60 segundos desde el último refresh
        if (now - lastRefreshRef.current >= 60000 && !refreshing && !loadingFlags.orders) {
          loadData(true) // true = silent refresh
        }
      }, 60000)
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [storeId, loadData, refreshing, loadingFlags.orders])

  // WebSocket: Recibir actualizaciones de pedidos en tiempo real
  useOrderUpdates(
    storeId,
    useCallback((updatedOrder: Order) => {
      // Normalizar el pedido actualizado
      const normalizedOrder: Order = {
        ...updatedOrder,
        items: (updatedOrder.items || []).map((item: any) => ({
          ...item,
          unit_price: Number(item.unit_price || item.unitPrice || 0),
          quantity: Number(item.quantity || 0)
        }))
      }

      // Actualizar el pedido en el estado
      setOrders(prevOrders => {
        const orderIndex = prevOrders.findIndex(o => o.id === normalizedOrder.id)
        if (orderIndex >= 0) {
          // Actualizar pedido existente
          const newOrders = [...prevOrders]
          newOrders[orderIndex] = normalizedOrder
          // Actualizar cache
          const cacheKey = `dispatcher_orders_${storeId}`
          setCache(cacheKey, newOrders, 30 * 1000)
          return newOrders
        } else {
          // Nuevo pedido, agregarlo al inicio
          const newOrders = [normalizedOrder, ...prevOrders]
          // Actualizar cache
          const cacheKey = `dispatcher_orders_${storeId}`
          setCache(cacheKey, newOrders, 30 * 1000)
          return newOrders
        }
      })

      // Mostrar notificación solo si el pedido cambió de estado
      const currentOrder = orders.find(o => o.id === normalizedOrder.id)
      if (currentOrder && currentOrder.status !== normalizedOrder.status) {
        toast.success(`Pedido #${normalizedOrder.id.substring(0, 8)} actualizado: ${normalizedOrder.status}`)
      }
    }, [storeId, orders]),
    useCallback((newOrder: Order) => {
      // Normalizar el nuevo pedido
      const normalizedOrder: Order = {
        ...newOrder,
        items: (newOrder.items || []).map((item: any) => ({
          ...item,
          unit_price: Number(item.unit_price || item.unitPrice || 0),
          quantity: Number(item.quantity || 0)
        }))
      }

      // Agregar nuevo pedido al inicio
      setOrders(prevOrders => {
        // Verificar que no exista ya
        if (prevOrders.find(o => o.id === normalizedOrder.id)) {
          return prevOrders
        }
        const newOrders = [normalizedOrder, ...prevOrders]
        // Actualizar cache
        const cacheKey = `dispatcher_orders_${storeId}`
        setCache(cacheKey, newOrders, 30 * 1000)
        return newOrders
      })

      toast.success(`Nuevo pedido recibido: #${normalizedOrder.id.substring(0, 8)}`)
    }, [storeId])
  )

  const handleAssign = (order: Order, type: 'chef' | 'delivery') => {
    setSelectedOrder(order)
    setAssignType(type)
    setShowAssignModal(true)
  }

  const confirmAssign = async (employeeId: string) => {
    if (!selectedOrder || !assignType) return

    try {
      setAssigningOrder(selectedOrder.id)
      const updateData: any = {
        status: assignType === 'chef' ? 'preparing' : selectedOrder.status === 'ready' ? 'delivering' : selectedOrder.status
      }

      if (assignType === 'chef') {
        updateData.assignedChefId = employeeId
      } else {
        updateData.assignedDeliveryId = employeeId
        if (selectedOrder.status === 'ready') {
          updateData.status = 'delivering'
        }
      }

      const response = await api.patch(`/orders/${selectedOrder.id}/status`, updateData)
      
      if (response.data.success) {
        toast.success(`${assignType === 'chef' ? 'Chef' : 'Delivery'} asignado exitosamente`)
        setShowAssignModal(false)
        setSelectedOrder(null)
        setAssignType(null)
        // Invalidar cache de orders y recargar
        removeCache(`dispatcher_orders_${storeId}`)
        loadOrders()
      } else {
        toast.error(response.data.message || 'Error al asignar')
      }
    } catch (error: any) {
      console.error('Error assigning:', error)
      toast.error(error.response?.data?.message || 'Error al asignar empleado')
    } finally {
      setAssigningOrder(null)
    }
  }

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      setUpdatingStatus(orderId)
      const response = await api.patch(`/orders/${orderId}/status`, { status: newStatus })
      
      if (response.data.success) {
        toast.success('Estado actualizado exitosamente')
        // Invalidar cache de orders y recargar
        removeCache(`dispatcher_orders_${storeId}`)
        await loadOrders()
      } else {
        toast.error(response.data.message || 'Error al actualizar estado')
      }
    } catch (error: any) {
      console.error('Error updating status:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar estado')
    } finally {
      setUpdatingStatus(null)
    }
  }

  const handleViewOrderDetails = (order: Order) => {
    navigate(`/dispatcher/orders/${order.id}`)
  }

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = searchTerm === '' || 
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone.includes(searchTerm) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  const pendingOrders = filteredOrders.filter(o => o.status === 'pending')
  const preparingOrders = filteredOrders.filter(o => o.status === 'preparing')
  const readyOrders = filteredOrders.filter(o => o.status === 'ready' || o.status === 'ready_for_pickup')
  const deliveringOrders = filteredOrders.filter(o => o.status === 'delivering' || o.status === 'picked_up')

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-700 font-light">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 pb-20">
      {/* Header - Fixed with scroll hide */}
      <div className={`fixed top-16 left-0 right-0 z-[9998] bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-lg shadow-black/5 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      } lg:translate-y-0`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-light text-gray-900 tracking-tight mb-1">Panel de Despacho</h1>
              {store && (
                <p className="text-sm sm:text-base text-gray-600 truncate">{store.name}</p>
              )}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => navigate('/dispatcher/pos')}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl sm:rounded-2xl shadow-md shadow-green-900/20 hover:shadow-lg hover:shadow-green-900/30 transition-all duration-200 font-medium text-xs sm:text-sm flex-1 sm:flex-initial active:scale-95"
              >
                <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Punto de Venta</span>
              </button>
              <button
                onClick={() => loadData(false)}
                disabled={refreshing}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl sm:rounded-2xl shadow-md shadow-gray-900/20 hover:shadow-lg hover:shadow-gray-900/30 transition-all duration-200 disabled:opacity-50 font-medium text-xs sm:text-sm flex-1 sm:flex-initial active:scale-95"
              >
                <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Actualizar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 pt-20 sm:pt-24 lg:pt-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-2 truncate">Pendientes</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-light text-amber-600">{pendingOrders.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-10 sm:h-12 sm:h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-2 truncate">Preparando</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-light text-blue-600">{preparingOrders.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-10 sm:h-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                <ChefHat className="w-4 h-4 sm:w-5 sm:h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-2 truncate">Listos</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-light text-indigo-600">{readyOrders.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-10 sm:h-12 sm:h-12 bg-gradient-to-br from-indigo-400 to-indigo-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20 flex-shrink-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 hover:shadow-2xl hover:shadow-orange-500/10 hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-2 truncate">En Camino</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-light text-orange-600">{deliveringOrders.length}</p>
              </div>
              <div className="w-9 h-9 sm:w-10 sm:h-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20 flex-shrink-0">
                <Truck className="w-4 h-4 sm:w-5 sm:h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por cliente, teléfono o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:border-gray-300 focus:bg-white/80 focus:outline-none text-sm transition-all duration-200"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:border-gray-300 focus:bg-white/80 focus:outline-none text-xs sm:text-sm font-medium transition-all duration-200"
              >
                <option value="all">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="preparing">Preparando</option>
                <option value="ready">Listo</option>
                <option value="delivering">En Camino</option>
              </select>
            </div>
          </div>
        </div>

        {/* Employee Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-light text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="truncate">Chefs Disponibles</span>
              </h2>
              <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100/50 px-2 sm:px-3 py-1 rounded-full border border-gray-200/50">{chefs.length} activos</span>
            </div>
            <div className="space-y-2">
              {chefs.length > 0 ? (
                chefs.map(chef => {
                  const assignedCount = orders.filter(o => o.assigned_chef_id === chef.id && o.status === 'preparing').length
                  return (
                    <EmployeeCard
                      key={chef.id}
                      employee={chef}
                      assignedCount={assignedCount}
                      type="chef"
                    />
                  )
                })
              ) : (
                <p className="text-gray-400 text-center py-4 text-xs sm:text-sm">No hay chefs disponibles</p>
              )}
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-light text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                  <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="truncate">Delivery Disponibles</span>
              </h2>
              <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100/50 px-2 sm:px-3 py-1 rounded-full border border-gray-200/50">{deliveryDrivers.length} activos</span>
            </div>
            <div className="space-y-2">
              {deliveryDrivers.length > 0 ? (
                deliveryDrivers.map(driver => {
                  const assignedCount = orders.filter(o => o.assigned_delivery_id === driver.id && o.status === 'delivering').length
                  return (
                    <EmployeeCard
                      key={driver.id}
                      employee={driver}
                      assignedCount={assignedCount}
                      type="delivery"
                    />
                  )
                })
              ) : (
                <p className="text-gray-400 text-center py-4 text-xs sm:text-sm">No hay delivery disponibles</p>
              )}
            </div>
          </div>
        </div>

        {/* Orders Sections */}
        <div className="space-y-3 sm:space-y-4">
          {/* Pending Orders */}
          {pendingOrders.length > 0 && (
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-6">
              <h2 className="text-base sm:text-lg font-light text-gray-900 mb-3 sm:mb-4 flex flex-wrap items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="truncate">Pedidos Pendientes</span>
                <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100/50 px-2 sm:px-3 py-1 rounded-full border border-gray-200/50">({pendingOrders.length})</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {pendingOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onAssignChef={() => handleAssign(order, 'chef')}
                    onAssignDelivery={() => handleAssign(order, 'delivery')}
                    onStatusChange={handleStatusChange}
                    onViewDetails={() => handleViewOrderDetails(order)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Preparing Orders */}
          {preparingOrders.length > 0 && (
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-6">
              <h2 className="text-base sm:text-lg font-light text-gray-900 mb-3 sm:mb-4 flex flex-wrap items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="truncate">En Preparación</span>
                <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100/50 px-2 sm:px-3 py-1 rounded-full border border-gray-200/50">({preparingOrders.length})</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {preparingOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onAssignChef={() => handleAssign(order, 'chef')}
                    onAssignDelivery={() => handleAssign(order, 'delivery')}
                    onStatusChange={handleStatusChange}
                    onViewDetails={() => handleViewOrderDetails(order)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Ready Orders */}
          {readyOrders.length > 0 && (
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-6">
              <h2 className="text-base sm:text-lg font-light text-gray-900 mb-3 sm:mb-4 flex flex-wrap items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-400 to-indigo-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="truncate">Listos para Entrega</span>
                <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100/50 px-2 sm:px-3 py-1 rounded-full border border-gray-200/50">({readyOrders.length})</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {readyOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onAssignChef={() => handleAssign(order, 'chef')}
                    onAssignDelivery={() => handleAssign(order, 'delivery')}
                    onStatusChange={handleStatusChange}
                    onViewDetails={() => handleViewOrderDetails(order)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Delivering Orders */}
          {deliveringOrders.length > 0 && (
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-6">
              <h2 className="text-base sm:text-lg font-light text-gray-900 mb-3 sm:mb-4 flex flex-wrap items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                  <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="truncate">En Camino</span>
                <span className="text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100/50 px-2 sm:px-3 py-1 rounded-full border border-gray-200/50">({deliveringOrders.length})</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {deliveringOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onAssignChef={() => handleAssign(order, 'chef')}
                    onAssignDelivery={() => handleAssign(order, 'delivery')}
                    onStatusChange={handleStatusChange}
                    onViewDetails={() => handleViewOrderDetails(order)}
                  />
                ))}
              </div>
            </div>
          )}

          {filteredOrders.length === 0 && !loading && (
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-8 sm:p-12 md:p-16 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <Package className="w-8 h-8 sm:w-10 sm:h-10 text-gray-500" />
              </div>
              <p className="text-gray-500 text-sm sm:text-base font-light">No hay pedidos disponibles</p>
            </div>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && selectedOrder && assignType && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl shadow-black/20 max-w-md w-full max-h-[90vh] overflow-y-auto border border-white/60">
            <div className="p-4 sm:p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-light text-gray-900">
                  Asignar {assignType === 'chef' ? 'Chef' : 'Delivery'}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignModal(false)
                    setSelectedOrder(null)
                    setAssignType(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 sm:mb-6 p-3 sm:p-4 md:p-5 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/50">
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-2">Pedido #{selectedOrder.id.substring(0, 8)}</p>
                <p className="font-medium text-gray-900 text-base sm:text-lg mb-1 truncate">{selectedOrder.customer_name}</p>
                <p className="text-xs sm:text-sm text-gray-600">RD${(selectedOrder.total_amount + selectedOrder.delivery_fee).toFixed(2)}</p>
              </div>

              <div className="space-y-2 sm:space-y-3 max-h-[50vh] sm:max-h-64 overflow-y-auto">
                {(assignType === 'chef' ? chefs : deliveryDrivers).map(employee => (
                  <button
                    key={employee.id}
                    onClick={() => confirmAssign(employee.id)}
                    disabled={assigningOrder === selectedOrder.id}
                    className="w-full p-3 sm:p-4 text-left bg-white/50 backdrop-blur-sm hover:bg-white/70 rounded-xl sm:rounded-2xl border border-gray-200/50 transition-all duration-200 disabled:opacity-50 hover:shadow-md"
                  >
                    <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{employee.name}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">{employee.email}</p>
                  </button>
                ))}
              </div>

              {((assignType === 'chef' ? chefs : deliveryDrivers).length === 0) && (
                <p className="text-center text-gray-500 py-4 text-sm">
                  No hay {assignType === 'chef' ? 'chefs' : 'delivery'} disponibles
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DispatcherDashboard

